import { useState, useContext, useEffect } from 'react';
import { Send, Sparkles, MessageCircle, History, ChevronDown } from 'lucide-react';
import { AuthContext, API } from '@/App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import VoiceInput from '@/components/VoiceInput';
import { trackDailyFeeling } from '@/lib/analytics';

const DailyFeelingWidget = () => {
  const { language } = useContext(AuthContext);
  const navigate = useNavigate();
  const [feeling, setFeeling] = useState('');
  const [loading, setLoading] = useState(false);
  const [todayFeeling, setTodayFeeling] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);

  const t = (es, ca) => language === 'ca' ? ca : es;

  // Check if user already submitted today
  useEffect(() => {
    checkTodayFeeling();
  }, []);

  const checkTodayFeeling = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/daily-feeling`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data) {
        setTodayFeeling(res.data);
      }
    } catch (error) {
      // No feeling today yet, that's okay
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!feeling.trim()) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/daily-feeling`, 
        { feeling: feeling.trim(), language: language || 'es' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTodayFeeling(res.data);
      trackDailyFeeling();
      toast.success(t('Compartido', 'Compartit'));
      setFeeling('');
      setJustSubmitted(true);
      setExpanded(true);
    } catch (error) {
      console.error('Error submitting feeling:', error);
      toast.error(t('Error al enviar', 'Error en enviar'));
    } finally {
      setLoading(false);
    }
  };

  // If already submitted today, show collapsible response
  if (todayFeeling) {
    return (
      <div className="bg-gradient-to-br from-slate-900/50 to-teal-900/30 rounded-2xl border border-teal-400/20 fade-in overflow-hidden">
        <button
          data-testid="toggle-feeling-btn"
          onClick={() => setExpanded(!expanded)}
          className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-teal-300" />
            <h3 className="text-lg font-semibold text-gray-200">
              {t('Tu sentir de hoy', 'El teu sentir d\'avui')}
            </h3>
            {todayFeeling.emotion_identified && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-teal-400/15 text-teal-300">
                {todayFeeling.emotion_identified}
              </span>
            )}
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>

        {expanded && (
          <div className="px-4 pb-4 space-y-3 fade-in">
            <div className="bg-black/20 rounded-xl p-3">
              <p className="text-gray-300 text-sm italic">"{todayFeeling.feeling}"</p>
            </div>

            {todayFeeling.ai_response && (
              <div className="bg-purple-900/20 rounded-xl p-3 border border-purple-400/10">
                <p className="text-gray-200 text-sm leading-relaxed">
                  {todayFeeling.ai_response}
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  navigate(`/chat?context=daily_feeling&id=${today}&title=${encodeURIComponent(t('Sentir de hoy', "Sentir d'avui"))}`);
                }}
                className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-xl bg-purple-900/30 border border-purple-400/20 hover:border-purple-400/40 text-purple-300 text-sm transition-all"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                <span>{t('Profundizar', 'Aprofundir')}</span>
              </button>
              <button
                onClick={() => navigate('/feeling-history')}
                className="flex items-center justify-center gap-2 py-2 px-3 rounded-xl bg-teal-900/30 border border-teal-400/20 hover:border-teal-400/40 text-teal-300 text-sm transition-all"
              >
                <History className="w-3.5 h-3.5" />
                <span>{t('Historial', 'Historial')}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-slate-900/50 to-teal-900/30 rounded-2xl p-6 border border-teal-400/20 fade-in">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-teal-200">
          {t('¿Cómo te sientes hoy?', 'Com et sents avui?')}
        </h2>
        <button
          data-testid="view-history-btn-empty"
          onClick={() => navigate('/feeling-history')}
          className="flex items-center gap-1 text-xs text-gray-400 hover:text-teal-300 transition-colors"
        >
          <History className="w-3 h-3" />
          <span>{t('Historial', 'Historial')}</span>
        </button>
      </div>
      <p className="text-sm text-gray-300/60 italic mb-4">
        {t('Tus emociones son mensajeras que traen información valiosa', 'Les teves emocions són missatgeres que porten informació valuosa')}
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative">
          <textarea
            data-testid="feeling-input"
            value={feeling}
            onChange={(e) => setFeeling(e.target.value)}
            placeholder={t(
              'Comparte cómo te sientes en este momento...',
              'Comparteix com et sents en aquest moment...'
            )}
            className="w-full px-4 py-3 pr-14 bg-black/30 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 transition-all resize-none"
            rows={3}
          />
          <div className="absolute bottom-3 right-3">
            <VoiceInput 
              onTranscription={(text) => setFeeling(feeling + (feeling ? ' ' : '') + text)}
              disabled={loading}
            />
          </div>
        </div>
        <button
          data-testid="submit-feeling-btn"
          type="submit"
          disabled={loading || !feeling.trim()}
          className="btn-primary flex items-center gap-2"
        >
          <Send className="w-5 h-5" />
          <span>{loading ? t('Enviando...', 'Enviant...') : t('Compartir', 'Compartir')}</span>
        </button>
      </form>
    </div>
  );
};

export default DailyFeelingWidget;
